import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { Check, ListPlus, Loader2, Pencil, Trash2, X } from "lucide-react";

const TUTAR_ALANLAR = [
  { key: "borc", label: "Borç" },
  { key: "alacak", label: "Alacak" },
  { key: "borc_bakiye", label: "Borç Bakiyesi" },
  { key: "alacak_bakiye", label: "Alacak Bakiyesi" },
];
const BOS_YENI = { kod: "", ad: "", borc: "", alacak: "", borc_bakiye: "", alacak_bakiye: "" };

export const MizanSekmesi = ({ yonetici, yil = 2026 }) => {
  const [satirlar, setSatirlar] = useState(null);
  const [duzenleId, setDuzenleId] = useState(null);
  const [duzenleForm, setDuzenleForm] = useState(null);
  const [yeni, setYeni] = useState(BOS_YENI);
  const [saving, setSaving] = useState(false);

  const fetchMizan = useCallback(async () => {
    try {
      const res = await api.get("/mizan", { params: { yil } });
      setSatirlar(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Mizan yüklenemedi."));
    }
  }, [yil]);

  useEffect(() => {
    fetchMizan();
  }, [fetchMizan]);

  const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

  const duzenleBaslat = (s) => {
    setDuzenleId(s.id);
    setDuzenleForm({
      kod: s.kod, ad: s.ad || "",
      borc: s.borc ?? "", alacak: s.alacak ?? "",
      borc_bakiye: s.borc_bakiye ?? "", alacak_bakiye: s.alacak_bakiye ?? "",
    });
  };

  const duzenleKaydet = async () => {
    if (!duzenleForm.kod.trim()) return toast.error("Hesap kodu zorunludur.");
    setSaving(true);
    try {
      await api.put(`/mizan/${duzenleId}`, {
        kod: duzenleForm.kod.trim(), ad: duzenleForm.ad.trim(),
        borc: num(duzenleForm.borc), alacak: num(duzenleForm.alacak),
        borc_bakiye: num(duzenleForm.borc_bakiye), alacak_bakiye: num(duzenleForm.alacak_bakiye),
      });
      toast.success("Mizan satırı güncellendi.");
      setDuzenleId(null);
      fetchMizan();
    } catch (err) {
      toast.error(getErrMsg(err, "Güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const yeniEkle = async () => {
    if (!yeni.kod.trim()) return toast.error("Hesap kodu zorunludur.");
    setSaving(true);
    try {
      await api.post(`/mizan?yil=${yil}`, {
        kod: yeni.kod.trim(), ad: yeni.ad.trim(),
        borc: num(yeni.borc), alacak: num(yeni.alacak),
        borc_bakiye: num(yeni.borc_bakiye), alacak_bakiye: num(yeni.alacak_bakiye),
      });
      toast.success("Hesap satırı eklendi.");
      setYeni(BOS_YENI);
      fetchMizan();
    } catch (err) {
      toast.error(getErrMsg(err, "Eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/mizan/${id}`);
      toast.success("Mizan satırı silindi.");
      fetchMizan();
    } catch (err) {
      toast.error(getErrMsg(err, "Silinemedi."));
    }
  };

  const hucre = (deger) =>
    deger === null || deger === undefined ? <span className="text-muted-foreground/50">—</span> : formatTL(deger, 2);

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="mizan-sekmesi">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold">Mizan — {yil} (hesap planı düzenlenebilir, tutarlar manuel girilir)</h3>
        {satirlar && <p className="text-xs text-muted-foreground">{satirlar.length} hesap satırı</p>}
      </div>

      {yonetici && (
        <div className="rounded-lg border p-3 grid grid-cols-2 lg:grid-cols-7 gap-2 items-end" data-testid="mizan-yeni-form">
          <Input placeholder="Hesap Kodu *" value={yeni.kod} onChange={(e) => setYeni((f) => ({ ...f, kod: e.target.value }))} className="h-10 tabular-nums" data-testid="mizan-yeni-kod-input" />
          <Input placeholder="Hesap Adı" value={yeni.ad} onChange={(e) => setYeni((f) => ({ ...f, ad: e.target.value }))} className="h-10 lg:col-span-2" data-testid="mizan-yeni-ad-input" />
          {TUTAR_ALANLAR.slice(0, 2).map((a) => (
            <Input key={a.key} type="number" step="0.01" placeholder={a.label} value={yeni[a.key]} onChange={(e) => setYeni((f) => ({ ...f, [a.key]: e.target.value }))} className="h-10 text-right tabular-nums" data-testid={`mizan-yeni-${a.key}-input`} />
          ))}
          <Button onClick={yeniEkle} disabled={saving} className="h-10 gap-1.5 lg:col-span-2" data-testid="mizan-yeni-ekle-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListPlus className="h-4 w-4" />} Hesap Satırı Ekle
          </Button>
        </div>
      )}

      {!satirlar ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2">Hesap Kodu</th>
                <th className="px-3 py-2">Hesap Adı</th>
                {TUTAR_ALANLAR.map((a) => <th key={a.key} className="px-3 py-2 text-right">{a.label}</th>)}
                {yonetici && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody data-testid="mizan-liste">
              {satirlar.map((s) => {
                const anaHesap = !s.kod.includes(".") && s.kod.length <= 3;
                if (duzenleId === s.id) {
                  return (
                    <tr key={s.id} className="border-t bg-sky-50/50" data-testid="mizan-duzenle-satiri">
                      <td className="px-2 py-1.5"><Input value={duzenleForm.kod} onChange={(e) => setDuzenleForm((f) => ({ ...f, kod: e.target.value }))} className="h-9 w-28 tabular-nums" data-testid="mizan-edit-kod-input" /></td>
                      <td className="px-2 py-1.5"><Input value={duzenleForm.ad} onChange={(e) => setDuzenleForm((f) => ({ ...f, ad: e.target.value }))} className="h-9 min-w-[180px]" data-testid="mizan-edit-ad-input" /></td>
                      {TUTAR_ALANLAR.map((a) => (
                        <td key={a.key} className="px-2 py-1.5">
                          <Input type="number" step="0.01" value={duzenleForm[a.key]} onChange={(e) => setDuzenleForm((f) => ({ ...f, [a.key]: e.target.value }))} className="h-9 w-28 text-right tabular-nums" data-testid={`mizan-edit-${a.key}-input`} />
                        </td>
                      ))}
                      <td className="px-2 py-1.5 whitespace-nowrap text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-700" onClick={duzenleKaydet} disabled={saving} data-testid="mizan-edit-kaydet-button" aria-label="Kaydet"><Check className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDuzenleId(null)} data-testid="mizan-edit-iptal-button" aria-label="Vazgeç"><X className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={s.id} className={`border-t border-border/50 ${anaHesap ? "bg-muted/25 font-semibold" : ""}`} data-testid={`mizan-satir-${s.kod}`}>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{s.kod}</td>
                    <td className="px-3 py-2">{s.ad}</td>
                    {TUTAR_ALANLAR.map((a) => (
                      <td key={a.key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{hucre(s[a.key])}</td>
                    ))}
                    {yonetici && (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => duzenleBaslat(s)} data-testid={`mizan-duzenle-${s.kod}`} aria-label="Satırı düzenle"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => sil(s.id)} data-testid={`mizan-sil-${s.kod}`} aria-label="Satırı sil"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
